/* eslint-disable max-len */
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CopyProductsModalContainerComponent } from '@app/lists/components/copy-products-modal-container/copy-products-modal-container.component';
import { MassDeleteDualPaneContainerComponent } from '@app/lists/shared/delete-list/mass-delete-dual-pane-container/mass-delete-dual-pane-container.component';
import { ListDownloadContainerComponent } from '@app/lists/shared/list-download-container/list-download-container.component';
import { ListPrintContainerComponent } from '@app/lists/shared/list-print-container/list-print-container.component';
import { NonUsfProductViewModelSelectors } from '@inventory/pages/manage-non-usf-products/selectors/non-usf-selectors';
import {
  ActionSheetController,
  PopoverController,
  ViewDidEnter,
  ViewDidLeave,
  ViewWillEnter,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { SimpleSearchModel } from '@shared/components/new-search-simple-modal/new-search-simple-modal.component';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { Button } from '@shared/models/ellipsis-option.model';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { ListKey } from '@usf/list-types';
import {
  DownloadListOptions,
  ListConstants,
  ListState,
  ListViewMode,
} from '@usf/ngrx-list';
import { LoadingState } from '@usf/ngrx-product';
import {
  Observable,
  Subscription,
  combineLatest,
  firstValueFrom,
  of,
} from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Crumb } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { EllipsisOptionsPopoverComponent } from '../../../shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import {
  ListActions,
  ListTypes,
  UsfListDetailManagementHeights,
} from '../../../shared/constants/lists-constants';
import { PATHS } from '../../../shared/constants/paths';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { NavigationHelperService } from '../../../shared/helpers/navigation.helpers.service';
import { Product } from '../../../shared/models/product.model';
import {
  EllipsisClickData,
  ProductClickData,
} from '../../../shared/models/usf-product-card.model';
import { ListProductCardService } from '../../../shared/services/product-card/list-product-card.service';
import { UserService } from '../../../user/services';
import { ListAnalyticsHelperService } from '../../services/list-analytics-helper.service';
import { ListsOverlayService } from '../../services/lists-overlay.service';
import {
  ItemTypes,
  ListManagementViewModel,
  ProductRow,
} from '../../shared/list-detail-management/model/list-detail-management-view.model';
import { SelectedItem } from '../../shared/list-detail-management/model/selected-product.model';
import { createListKey } from '../../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { ListDetailManagementService } from '../../shared/list-detail-management/services/list-detail-management.service';
import { ListGroupsDesktopTabletComponent } from '../../shared/list-groups/desktop-tablet/list-groups-desktop-tablet.component';

@Component({
  selector: 'app-list-management',
  templateUrl: './list-management.page.html',
  styleUrls: [
    '../lists-pages-common-styles.scss',
    './list-management.page.scss',
  ],
})
export class ListManagementPage
  implements ViewWillEnter, ViewDidEnter, ViewDidLeave
{
  // Children components
  @ViewChild('listGroups') listGroups: ListGroupsDesktopTabletComponent;
  @ViewChild('sidebar') sidebar: ElementRef;
  @ViewChild('sidebarGroupsSection') sidebarGroupsSection: ElementRef;
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild(MassDeleteDualPaneContainerComponent)
  massDeleteModal: MassDeleteDualPaneContainerComponent;
  @ViewChild(CopyProductsModalContainerComponent)
  copyProductsModal: CopyProductsModalContainerComponent;
  @ViewChild(ListDownloadContainerComponent)
  listDownloadModal: ListDownloadContainerComponent;
  @ViewChild(ListPrintContainerComponent)
  listPrintModal: ListPrintContainerComponent;

  // Constants
  mode: UsfProductCardModeEnum = UsfProductCardModeEnum.listManagement;
  readonly ItemTypes = ItemTypes;
  readonly ListTypes = ListTypes;
  readonly ListActions = ListActions;

  // Component variables
  vm: ListManagementViewModel;
  listKey = this.listDetailManagementService.getListIdFromRoute(
    this.route.snapshot,
  );
  sidebarTopPosition: number;
  selectedSort = ListConstants.groupLine;
  selectedFilters: ListConstants[] = [];
  selectedGroup: string = ListConstants.allGroups;
  breadcrumbs: Crumb[];
  showTopButton = false;
  copyListEnabled: boolean;
  searchKey = '';
  previousSearchKey = '';
  isLoading = true;
  hasFoundDefaultSelectedGroup = false;
  isSearchModalOpen = false;
  addProductsActionSheetIsOpen = false;
  isNewSearchEnabled = false;

  // Subscriptions
  isNewSearchEnabledSubscription$: Subscription;
  listDetailManagementSub$: Subscription = new Subscription();
  virtualScrollSub$: Subscription = new Subscription();
  addProductsButtons$: Observable<Button[]> = of([]);
  totalNonUsfProducts$: Observable<number>;
  addNonUsfListFlag$: Observable<boolean>;

  constructor(
    private route: ActivatedRoute,
    private renderer: Renderer2,
    readonly panAppState: PanAppState,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
    public platformService: PlatformService,
    public translateService: TranslateService,
    private listAnalyticsHelperService: ListAnalyticsHelperService,
    public listDetailManagementService: ListDetailManagementService,
    private listProductCardService: ListProductCardService,
    public listsOverlayService: ListsOverlayService,
    private navigationHelperService: NavigationHelperService,
    private router: Router,
    private userService: UserService,
    private nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    private toggleNewSearchService: ToggleNewSearchService,
    readonly addToListModalService: AddToListModalService,
  ) {
    this.totalNonUsfProducts$ =
      this.nonUsfProductViewModelSelectors.totalNonUsfProducts$;
  }

  ionViewWillEnter() {
    this.isNewSearchEnabledSubscription$ = this.toggleNewSearchService
      .isNewSearchEnabled()
      .subscribe(newSearchEnabled => {
        this.isNewSearchEnabled = newSearchEnabled;
      });
    this.hasFoundDefaultSelectedGroup = false;
    this.updateListForUserPreferences();
    this.updateLastSelectedList();
    this.setPageTitle();
    this.setVirtualScrollSub();
    this.setBreadcrumbs();
    this.getViewModel();
    this.resetSearch();
    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(isEnabled => (this.copyListEnabled = isEnabled));
    this.setCopyModal();
    this.addNonUsfListFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_add_non_usf_list.name,
    ]);
    this.getAddProductsButtons$();
  }

  resetSelectedGroup = () => {
    this.listDetailManagementService.changeSelectedGroup(
      this.listKey?.listTypeId + '-' + this.listKey?.listId,
      this.vm.listGroups[0].groupName,
    );
  };

  updateListForUserPreferences() {
    this.listDetailManagementService.updateRecentlyViewedUserPreferences(
      this.listKey?.listTypeId + '-' + this.listKey?.listId,
    );
  }

  updateLastSelectedList() {
    this.listDetailManagementService.updateLastSelectedList(
      this.listKey?.listTypeId,
      this.listKey?.listId,
    );
  }

  setCopyModal() {
    this.listDetailManagementService.CopyModalRef = this.copyProductsModal;
  }

  releaseCopyModal() {
    this.listDetailManagementService.CopyModalRef = undefined;
  }

  setPageTitle() {
    this.panAppState.setPageTitle(
      this.vm?.list?.listKey?.listTypeId === ListTypes.og
        ? this.translateService.instant('i18n.lists.orderGuide')
        : !this.vm?.selectedGroup &&
            this.platformService.platformType ===
              this.platformService.platformEnum.mobile
          ? this.vm?.list?.listName
          : this.translateService.instant('i18n.lists.myLists'),
    );
  }

  setVirtualScrollSub() {
    this.sidebarTopPosition =
      this.platformService?.platformType ===
      this.platformService.platformEnum.desktop
        ? UsfListDetailManagementHeights.groupsSidebarBaseTopPositionDesktop
        : UsfListDetailManagementHeights.groupsSidebarBaseTopPositionTablet;

    this.virtualScrollSub$ = this.virtualScroll
      ?.elementScrolled()
      ?.subscribe(scrollEvent => {
        this.showTopButton =
          this.virtualScroll.measureScrollOffset('top') > 5 ? true : false;
        if (
          this.platformService.platformType !==
          this.platformService.platformEnum.mobile
        ) {
          const event = scrollEvent as any;
          this.adjustSidebarTop(event);

          if (
            this.platformService.platformType ===
            this.platformService.platformEnum.desktop
          ) {
            this.adjustSidebarHeight(event);
          }
        }
      });
  }

  adjustSidebarTop(event: any) {
    let newTopPosition =
      this.listDetailManagementService.calculateSidebarTopPosition(
        this.platformService.platformType,
        true,
        event?.target?.scrollTop,
      );
    let numberToCompare: number;
    let numberToSubtract: number;
    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop
    ) {
      // sidebar height + footer height + virtualscroll's scrollTop
      numberToCompare =
        UsfListDetailManagementHeights.groupsSidebarBaseHeightDesktopTablet +
        UsfListDetailManagementHeights.footerHeightDesktop +
        event?.target?.scrollTop;
      // sidebar height + footer height + virtualscroll's scrollTop - virtualscroll's scrollHeight + sidebar top position
      numberToSubtract =
        UsfListDetailManagementHeights.groupsSidebarBaseHeightDesktopTablet +
        UsfListDetailManagementHeights.footerHeightDesktop +
        event?.target?.scrollTop -
        event?.target?.scrollHeight +
        UsfListDetailManagementHeights.groupsSidebarBaseTopPositionDesktop;
    } else {
      // sidebar height + virtual scroll's scrollTop
      numberToCompare =
        UsfListDetailManagementHeights.groupsSidebarBaseHeightDesktopTablet +
        event?.target?.scrollTop;
      // sidebar height + virtualscroll's scrollTop - virtualscroll's scrollHeight + sidebar top position
      numberToSubtract =
        UsfListDetailManagementHeights.groupsSidebarBaseHeightDesktopTablet +
        event?.target?.scrollTop -
        event?.target?.scrollHeight +
        UsfListDetailManagementHeights.groupsSidebarBaseTopPositionTablet;
    }
    if (numberToCompare > event?.target?.scrollHeight) {
      newTopPosition -= numberToSubtract;
    }
    this.setSidebarTopStyle(newTopPosition);
  }

  setSidebarTopStyle(newTopPosition: number) {
    if (newTopPosition !== this.sidebarTopPosition) {
      this.sidebarTopPosition = newTopPosition;
      this.renderer?.setStyle(
        this.sidebar?.nativeElement,
        this.translateService.instant('i18n.common.measurements.top'),
        this.sidebarTopPosition.toString() +
          this.translateService.instant('i18n.common.measurements.px'),
      );
    }
  }

  adjustSidebarHeight(event: any) {
    if (window?.innerHeight > 800) {
      const el = event?.target
        ? event.target
        : this.virtualScroll?.elementRef?.nativeElement;

      const numberToCompare =
        this.sidebarGroupsSection?.nativeElement?.children[2]?.offsetHeight +
        UsfListDetailManagementHeights.groupsSidebarBaseTopPositionDesktop +
        UsfListDetailManagementHeights.footerHeightDesktop +
        el?.scrollTop;

      if (numberToCompare > el?.scrollHeight) {
        const newHeight = UsfListDetailManagementHeights.groupsSectionMaxHeight;
        this.setSidebarHeight(newHeight);
      } else if (
        numberToCompare +
          UsfListDetailManagementHeights.groupsSidebarIncrementDesktop <
          el?.scrollHeight &&
        this.sidebarGroupsSection?.nativeElement?.children[2]?.offsetHeight <=
          UsfListDetailManagementHeights.groupsSidebarBaseHeightDesktopTablet
      ) {
        const newHeight =
          this.sidebarGroupsSection?.nativeElement?.children[2]?.offsetHeight +
          UsfListDetailManagementHeights.groupsSidebarIncrementDesktop;
        this.setSidebarHeight(newHeight);
      }
    }
  }

  setSidebarHeight(newHeight: number) {
    this.renderer?.setStyle(
      this.sidebarGroupsSection?.nativeElement.children[2],
      this.translateService.instant('i18n.common.measurements.height'),
      newHeight.toString() +
        this.translateService.instant('i18n.common.measurements.px'),
    );
  }

  setBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myLists',
        url: '/desktop/lists',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.shoppingLists',
        url: '',
        active: true,
      },
    ];
  }

  scrollToTopOfGroupSidebar() {
    const groupScrollId = 'list-groups-section';
    const scroll = document.getElementById(groupScrollId);
    if (scroll) {
      let i = 0;
      let interval;
      interval = setInterval(() => {
        scroll.scrollTop = 0;
        i++;
        if (i === 10) {
          clearInterval(interval);
        }
      }, 10);
    }
  }

  getViewModel() {
    this.listDetailManagementSub$ = this.listDetailManagementService
      .selectListManagementViewModel$(
        this.listKey.listTypeId,
        this.listKey.listId,
      )
      .subscribe(viewModel => {
        this.vm = viewModel;
        if (
          !this.hasFoundDefaultSelectedGroup &&
          !!this.vm &&
          this.vm.listGroups.length > 0
        ) {
          this.hasFoundDefaultSelectedGroup = true;
          this.resetSelectedGroup();
        }
      });
  }

  ionViewDidEnter() {
    window.dispatchEvent(new Event('resize'));
    this.trackPageLoad();
    this.scrollToTopOfGroupSidebar();
  }

  trackPageLoad() {
    this.listDetailManagementService
      .selectListProductDataLoadingState$(
        this.listKey.listTypeId,
        this.listKey.listId,
      )
      .pipe(
        filter(
          loadingState =>
            loadingState === LoadingState.loaded ||
            loadingState === LoadingState.error,
        ),
        take(1),
      )
      .subscribe(loadingState => {
        if (loadingState === LoadingState.loaded) {
          this.handleSuccessfulPageLoad();
        }
        if (loadingState === LoadingState.error) {
          this.handleErrorForPageLoad();
        }
      });
  }

  handleSuccessfulPageLoad() {
    this.listAnalyticsHelperService.trackListManagementPageLoad(
      this.listKey.listTypeId + '-' + this.listKey.listId,
    );
    this.isLoading = false;
  }

  handleErrorForPageLoad() {
    this.listsOverlayService.showListLoadingErrorToast();
    this.navigationHelperService.routeTo('/lists');
  }

  ionViewDidLeave() {
    this.handleSubscriptions();
    this.resetSelectedProducts();
    this.resetSearch();
  }

  handleSubscriptions() {
    this.listDetailManagementSub$.unsubscribe();
    this.virtualScrollSub$.unsubscribe();
    this.isNewSearchEnabledSubscription$?.unsubscribe();
  }

  resetSelectedProducts() {
    this.listDetailManagementService.setAllSelectedProductCards(false, null);
  }

  goToTop() {
    this.platformService?.platformType ===
    this.platformService.platformEnum.desktop
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  updateSelectedProducts(
    listKey: ListKey,
    groupId: number,
    itemId: number,
    currentValue: boolean,
  ) {
    this.listDetailManagementService.setSelectedProductCards(
      listKey.listTypeId + '-' + listKey.listId + '-' + groupId + '-' + itemId,
      !currentValue,
    );
  }

  selectAllProducts() {
    this.listDetailManagementService.setAllSelectedProductCards(
      true,
      this.vm?.selectedGroup?.listGroupKey,
    );
  }

  deselectAllProducts() {
    this.listDetailManagementService.setAllSelectedProductCards(false, null);
  }

  populateSelectedProductCards() {
    const selectedProducts: SelectedItem[] = [];
    this.vm?.items?.map((productRow: ProductRow) => {
      if (productRow.itemType === ItemTypes.product) {
        if (productRow?.isChecked && productRow?.isChecked !== undefined) {
          selectedProducts.push({
            product: productRow?.summary,
            item: productRow?.item,
          });
        }
      }
    });
    return selectedProducts;
  }

  populateSelectedProductsForCopyProductModal() {
    const selectedProducts: ProductRow[] = [];
    this.vm?.items?.map((productRow: ProductRow) => {
      if (productRow.itemType === ItemTypes.product) {
        if (productRow?.isChecked && productRow?.isChecked !== undefined) {
          selectedProducts.push(productRow);
        }
      }
    });
    return selectedProducts;
  }

  onClickAddGroup() {
    if (
      this.platformService?.platformType ===
      this.platformService.platformEnum.desktop
    ) {
      this.addNewGroupDesktop();
    } else {
      this.openCreateGroupModal();
    }
  }

  addNewGroupDesktop() {
    this.listGroups.insertNewGroupCard();
    this.adjustSidebarHeight(null);
    try {
      setTimeout(() => {
        this.sidebarGroupsSection.nativeElement.childNodes[2].scrollTop =
          this.sidebarGroupsSection.nativeElement.childNodes[2].scrollHeight;
      }, 1);
    } catch (error) {}
  }

  async openCreateGroupModal() {
    const ret = this.listsOverlayService.openCreateGroupModal(
      this.vm?.list,
      this.vm?.listGroups,
      this.vm?.list?.listKey?.listId,
      this.vm?.list?.listKey?.listTypeId,
    );

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile
    ) {
      this.virtualScroll.scrollTo({ bottom: 0, behavior: 'smooth' });
    }

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.tablet
    ) {
      (await ret).onDidDismiss().then(data => {
        if (data?.data) {
          try {
            setTimeout(() => {
              this.sidebarGroupsSection.nativeElement.childNodes[2].scrollTop =
                this.sidebarGroupsSection.nativeElement.childNodes[2].scrollHeight;
            }, 1);
          } catch (error) {}
        }
      });
    }
  }

  async openSelectedProductsModal(action: string) {
    const copyAcrossListsFlag = await this.panAppState.featureEnabled(
      FEATURES.split_global_copy_products_across_lists.name,
    );
    const platformType = this.platformService.getPlatformType();
    if (
      copyAcrossListsFlag &&
      action === ListActions.copy &&
      platformType !== PlatformEnum.mobile
    ) {
      const selectedProducts =
        this.populateSelectedProductsForCopyProductModal();
      this.listsOverlayService.openCopyProductModal(
        selectedProducts,
        this.vm.list,
        this.vm.listGroups,
        this.vm.selectedGroup,
        this.vm.listItems,
        this.copyProductsModal,
      );
    } else {
      const selectedProducts = this.populateSelectedProductCards();
      const ret = this.listsOverlayService.openSelectedProductsModal(
        action,
        selectedProducts,
        this.vm.list,
        this.vm.listGroups,
        this.vm.selectedGroup,
        this.vm.listItems,
        copyAcrossListsFlag,
      );
      (await ret).onDidDismiss().then(data => {
        let modalData = data['data'];
        if (modalData?.selectedAction === ListActions.delete) {
          this.listDetailManagementService.deleteProducts(
            this.vm.list,
            modalData.selectedGroup,
            selectedProducts,
          );
        }
      });
    }
  }

  // LIST AND GROUP MANAGEMENT OPTIONS
  async openManagementOptions(event: Event, buttons: any[]) {
    if (!this.platformService.isTouch.value) {
      const listOptionsPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'ellipsis-options-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await listOptionsPopover.present();
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const listOptionsActionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await listOptionsActionSheet.present();
    }
  }

  // LIST OPTIONS
  openListOptions(event: Event) {
    const buttons = this.constructManagementButtons();
    this.openManagementOptions(event, buttons);
  }

  constructManagementButtons() {
    const buttons = [];
    this.appendEditListTypeButtons(buttons);
    this.appendEditListNameAndDeleteListButtons(buttons);
    if (this.vm?.list?.listItemCount > 0) {
      this.appendDownloadPrintButtons(
        buttons,
        this.vm.downloadPrintFlag,
        this.listDownloadModal,
        this.listPrintModal,
      );
    }
    return buttons;
  }

  /* Checks edit list type entitlements, and returns array of buttons that should go in popup **/
  appendEditListTypeButtons(buttons: any[]) {
    const list = this.vm?.list;
    const listIsPrivate = list?.listState?.toUpperCase() === 'PRIVATE';
    const listIsInternal = list?.listState?.toUpperCase() === 'INTERNAL';
    if (listIsPrivate) {
      buttons.push(this.constructSetListInternalButton(list));
    } else if (listIsInternal) {
      buttons.push(
        this.constructSetListPrivateButton(list),
        this.constructSetListPublicButton(list),
      );
    }
  }

  appendEditListNameAndDeleteListButtons(buttons: any[]) {
    buttons.push(this.constructEditListNameButton());

    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(flagSet => {
        if (flagSet && this.vm.list.listItemCount > 0) {
          buttons.push(
            this.constructCopyListButton(this.vm?.list),
            this.constructDeleteListButton(),
          );
        } else {
          buttons.push(this.constructDeleteListButton());
        }
      });
  }

  constructSetListInternalButton(list: ListState) {
    return {
      text: this.translateService.instant('i18n.lists.setListInternal'),
      handler: () => {
        this.listDetailManagementService.editListType('INTERNAL', list);
        this.dismissListOptions();
      },
    };
  }

  constructSetListPrivateButton(list: ListState) {
    return {
      text: this.translateService.instant('i18n.lists.setListPrivate'),
      handler: () => {
        this.listDetailManagementService.editListType('PRIVATE', list);
        this.dismissListOptions();
      },
    };
  }

  private appendDownloadPrintButtons(
    buttons: any[],
    downloadPrintFlag: boolean,
    listDownloadModal?: ListDownloadContainerComponent,
    listPrintModal?: ListPrintContainerComponent,
  ) {
    if (
      downloadPrintFlag &&
      this.platformService.platformType === PlatformEnum.mobile
    ) {
      buttons.push(
        {
          text: this.translateService.instant('i18n.common.download'),
          handler: () => {
            this.listsOverlayService.openDownloadListModal(
              this.vm?.list?.listKey?.listId,
              this.vm?.list?.listKey?.listTypeId,
              this.vm?.list?.listName,
              this.listDownloadModal,
            );
          },
        },
        {
          text: this.translateService.instant('i18n.lists.print'),
          handler: () => {
            this.listsOverlayService.openPrintListModal(
              this.vm?.list?.listKey?.listId,
              this.vm?.list?.listKey?.listTypeId,
              this.vm?.list?.listName,
              this.vm?.list?.customerNumber,
              this.vm?.list?.listState,
              this.listPrintModal,
            );
          },
        },
      );
    }
  }

  constructSetListPublicButton(list: ListState) {
    return {
      text: this.translateService.instant('i18n.lists.setListPublic'),
      handler: () => {
        this.listDetailManagementService.editListType('PUBLIC', list);
        this.dismissListOptions();
      },
    };
  }

  constructEditListNameButton() {
    return {
      text: this.translateService.instant('i18n.lists.editListName'),
      handler: () => {
        this.openEditNameModal(this.vm?.list?.listName, false);
        this.dismissListOptions();
      },
    };
  }

  constructCopyListButton(list: ListState) {
    return {
      text: this.translateService.instant('i18n.lists.copy'),
      handler: () => {
        this.listsOverlayService.openCopyListModal(list);
        this.dismissListOptions();
      },
    };
  }

  constructDeleteListButton() {
    return {
      text: this.translateService.instant('i18n.lists.deleteList'),
      handler: () => {
        this.openDeleteListModal();
        this.dismissListOptions();
      },
    };
  }

  // GROUP OPTIONS
  openGroupManagementOptions(event: Event) {
    const buttons = [];
    buttons.push(
      this.constructEditGroupNameButton(),
      this.constructDeleteGroupButton(),
    );
    this.openManagementOptions(event, buttons);
  }

  constructEditGroupNameButton() {
    return {
      text: this.translateService.instant('i18n.lists.editGroupName'),
      handler: () => {
        this.openEditNameModal(this.vm?.selectedGroup?.groupName, true);
        this.dismissListOptions();
      },
    };
  }

  constructDeleteGroupButton() {
    return {
      text: this.translateService.instant('i18n.lists.deleteGroup'),
      handler: () => {
        this.openDeleteGroupModal();
        this.dismissListOptions();
      },
    };
  }

  async openEditNameModal(name: string, isGroup: boolean) {
    this.listsOverlayService.openEditNameModal(
      name,
      this.vm?.list,
      this.vm?.listGroups,
      isGroup,
    );
  }

  async openDeleteListModal() {
    const massDeleteFlag = await this.panAppState.featureEnabled(
      FEATURES.split_global_mass_delete_list.name,
    );
    const showMassDelete = await firstValueFrom(
      this.userService.showMassDelete$(),
    );
    const isSuperUser = await firstValueFrom(this.userService.isSuperUser$());
    if (
      massDeleteFlag &&
      !isSuperUser &&
      this.massDeleteModal &&
      showMassDelete
    ) {
      this.listsOverlayService.openMassDeleteModal(
        this.vm?.list,
        this.massDeleteModal,
      );
    } else {
      await this.listsOverlayService.openDeleteListModal(this.vm?.list, true);
    }
  }

  async openDeleteGroupModal() {
    const modal = await this.listsOverlayService.openDeleteGroupModal(
      this.vm?.selectedGroup,
    );
    modal.onDidDismiss().then(data => {
      if (data?.data?.deleteAllProducts !== undefined) {
        this.listDetailManagementService.deleteGroup(
          this.vm?.list,
          this.vm?.selectedGroup,
          this.vm?.unassignedGroup,
          this.vm?.listItems,
          data?.data?.deleteAllProducts,
        );
      }
    });
  }

  async openQuickView(ellipsisClickData: EllipsisClickData) {
    this.listProductCardService.openQuickViewModal(
      ellipsisClickData,
      'quickView',
    );
  }

  search() {
    if (this.searchKey !== this.previousSearchKey) {
      this.listDetailManagementService.searchInList(
        this.searchKey,
        this.listKey.listId,
        this.listKey.listTypeId,
      );
      if (this.vm?.numProductsSelected > 0) {
        this.listDetailManagementService.setAllSelectedProductCards(
          false,
          null,
        );
      }
      this.previousSearchKey = this.searchKey;
    }
  }

  async resetSearch() {
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.listProductCardService.showProductStatuses(ellipsisClickData);
  }

  onAddProductAction() {
    this.listDetailManagementService.goToSearchPage();
  }

  getAddProductsButtons$() {
    this.addProductsButtons$ = combineLatest([
      this.totalNonUsfProducts$,
      this.addNonUsfListFlag$,
    ]).pipe(
      map(([productCount, flag]) => {
        const addProductsButtons: Button[] = [
          {
            text: this.translateService.instant('i18n.lists.addToListSearch'),
            handler: () => {
              if (this.isNewSearchEnabled) {
                this.isSearchModalOpen = true;
              } else {
                this.listsOverlayService.executeSearch();
              }
            },
          },
          {
            text: this.translateService.instant('i18n.lists.addToListMultiple'),
            handler: () => {
              this.router.navigate([PATHS.LISTS_SEARCH_PRODUCT_NUMBERS]);
            },
          },
        ];
        if (productCount > 0 && flag) {
          addProductsButtons.push({
            text: this.translateService.instant(
              'i18n.inventory.inventoryWorksheet.addNonUSFoodsProducts',
            ),
            handler: () => {
              this.addToListModalService.openModalForNonUsf();
            },
          });
        }
        if (this.platformService.platformType !== PlatformEnum.desktop) {
          addProductsButtons.push({
            text: this.translateService.instant('i18n.common.cancel'),
            role: 'cancel',
            cssClass: 'cancel-button',
          });
        }
        return addProductsButtons;
      }),
    );
  }

  onAddProductClick = () => {
    if (this.platformService.platformType !== PlatformEnum.desktop) {
      this.addProductsActionSheetIsOpen = true;
    }
  };

  handleSimpleSearchModalEvent(event: SimpleSearchModel) {
    this.isSearchModalOpen = false;
    if (event?.isExecuteSearch) {
      this.listsOverlayService.executeSearch(
        SEARCH_PAGES_ENUM.listDetail,
        event?.searchText,
      );
    }
  }

  resequenceProduct(event: any) {
    this.listDetailManagementService.moveItemFromSequenceNumber(
      event.newSeq,
      event.oldSeq,
      event.productNum,
      this.vm?.list,
      this.vm?.listGroups,
      this.vm?.listItems,
    );
  }

  resequenceGroup(event: CdkDragDrop<number, number>) {
    this.listDetailManagementService.resequenceGroup(
      this.vm?.list,
      this.vm?.listGroups[event.previousIndex],
      event.previousIndex,
      event.currentIndex,
    );
  }

  changeSelectedGroup(groupName: string) {
    this.goToTop();
    this.listDetailManagementService.changeSelectedGroup(
      createListKey(this.vm?.list?.listKey),
      groupName,
    );
    this.listDetailManagementService.setAllSelectedProductCards(false, null);
  }

  createGroup(groupName: string) {
    this.listDetailManagementService.createGroup(this.vm?.list, groupName);
  }

  async openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    const buttons =
      await this.listProductCardService.constructEllipsisButtons(
        ellipsisClickData,
      );
    this.listProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  async dismissListOptions() {
    this.listsOverlayService.dismissListOptions();
  }

  async routeToPDP(productClickData: ProductClickData) {
    this.listProductCardService.routeToPDP(
      productClickData.productNumber,
      productClickData.isQuickView,
    );
  }

  async openItemNotes(ellipsisClickData: EllipsisClickData) {
    const noteExists =
      ellipsisClickData.product.item?.note !== undefined &&
      ellipsisClickData.product.item?.note !== '' &&
      ellipsisClickData.product.item?.note !== null;
    if (this.vm?.listItemNoteFeatureFlag) {
      this.listProductCardService.directlyOpenCreateEditNote(
        noteExists,
        ellipsisClickData,
      );
    } else {
      this.listProductCardService.openQuickViewModal(
        ellipsisClickData,
        'quickView',
      );
    }
  }

  closeDownloadListModal() {
    this.listsOverlayService.closeDownloadListModal();
  }

  downloadList(options: DownloadListOptions) {
    this.listsOverlayService.closeDownloadListModal();
    this.listDetailManagementService.downloadList(options);
  }

  openDownloadPrintModal(download: boolean) {
    const listName = this.vm.list?.listName;
    if (download) {
      this.listsOverlayService.openDownloadListModal(
        this.vm.list.listKey.listId,
        this.vm.list.listKey.listTypeId,
        listName,
        this.listDownloadModal,
      );
    } else {
      this.listsOverlayService.openPrintListModal(
        this.vm.list.listKey.listId,
        this.vm.list.listKey.listTypeId,
        listName,
        this.vm.list.customerNumber,
        this.vm.list?.listState,
        this.listPrintModal,
      );
    }
  }

  handleProductConversionClick(product: Product) {
    this.listProductCardService.handleProductConversionClick(product, true);
  }

  getAccessibleListDataForUser$ = async () => {
    return await this.listDetailManagementService.requestLoadAndGiveAccessibleListData$();
  };

  getAccessibleListLoaded$ = () => {
    return this.listDetailManagementService.getIsAccessibleLoaded$();
  };

  finalSubmissionForCopyProductsToList = (
    targetListKeys: ListKey[],
    productNumbers: number[],
  ) => {
    return this.listDetailManagementService.submitCopyToListData(
      targetListKeys,
      productNumbers,
    );
  };

  enableCopyToList = async () => {
    return await this.listDetailManagementService.enableCopyToList();
  };

  hideCopyToListOption = async () => {
    return await this.listDetailManagementService.hideCopyToListOption();
  };
}
